import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Check, ExternalLink } from 'lucide-react';
import { useToast, PageHeader, Button, StatusBadge, Skeleton } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const PLANS = [
  { key: 'starter', name: 'Starter', blurb: 'For a single outlet finding its feet.', features: ['1 outlet', 'Core POS + KDS', 'Email support'] },
  { key: 'pro', name: 'Pro', blurb: 'For growing multi-outlet operations.', features: ['Multiple outlets', 'AI insights', 'Priority support'] },
  { key: 'enterprise', name: 'Enterprise', blurb: 'For larger chains with custom needs.', features: ['Unlimited outlets', 'Higher AI quota', 'Dedicated support'] },
];

const STATUS_VARIANT = {
  trialing: 'primary',
  active: 'success',
  past_due: 'warning',
  canceled: 'danger',
  incomplete: 'neutral',
};

const authHeaders = () => ({ headers: { 'x-auth-token': localStorage.getItem('token') } });

const Billing = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const canManageBilling = ['super_admin', 'admin'].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/billing/status', authHeaders());
      setStatus(res.data);
    } catch (err) {
      toast.error('Could not load billing status');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      toast.success('Subscription updated — thanks!');
      fetchStatus();
      setSearchParams({}, { replace: true });
    } else if (checkout === 'cancelled') {
      toast.info('Checkout cancelled — no changes made');
      setSearchParams({}, { replace: true });
    }
    // Deliberately only reacting to the URL param, not re-running when
    // fetchStatus/toast/setSearchParams identities change — this should
    // fire exactly once for whatever ?checkout= value the redirect landed
    // with, not on every re-render.
    // eslint-disable-next-line
  }, [searchParams]);

  const startCheckout = async (plan) => {
    setBusyPlan(plan);
    try {
      const res = await axios.post('/api/billing/checkout-session', { plan }, authHeaders());
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not start checkout');
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await axios.post('/api/billing/portal-session', {}, authHeaders());
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not open billing portal');
      setPortalBusy(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Billing" subtitle="Manage your subscription plan." />
        <Skeleton className="h-40 w-full max-w-2xl" />
      </div>
    );
  }

  const trialEndsAt = status?.trialEndsAt ? new Date(status.trialEndsAt) : null;
  const currentPeriodEnd = status?.currentPeriodEnd ? new Date(status.currentPeriodEnd) : null;
  const isTrialing = status?.status === 'trialing';
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : null;

  return (
    <div>
      <PageHeader
        title="Billing"
        subtitle="Manage your subscription plan and payment details."
        action={canManageBilling && status?.hasStripeCustomer && (
          <Button variant="secondary" leftIcon={<ExternalLink size={14} />} loading={portalBusy} onClick={openPortal}>
            Manage billing
          </Button>
        )}
      />

      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-6 max-w-2xl mb-6">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={16} className="text-primary-600" />
          <h2 className="text-sm font-semibold text-neutral-800">Current plan</h2>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-lg font-bold text-neutral-900 capitalize">{status?.plan || 'trial'}</span>
          <StatusBadge variant={STATUS_VARIANT[status?.status] || 'neutral'} dot>
            {status?.status || 'unknown'}
          </StatusBadge>
        </div>
        {isTrialing && trialEndsAt && (
          <p className="mt-2 text-xs text-neutral-500">
            {trialDaysLeft > 0
              ? `Your free trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} (${trialEndsAt.toLocaleDateString()}). Choose a plan below to keep going without interruption.`
              : `Your free trial has ended. Choose a plan below to keep using NexifyPOS.`}
          </p>
        )}
        {status?.status === 'past_due' && (
          <p className="mt-2 text-xs text-warning-700">
            Your last payment failed. Update your payment method to avoid losing access.
          </p>
        )}
        {status?.status === 'canceled' && (
          <p className="mt-2 text-xs text-danger-700">
            Your subscription has been cancelled. Choose a plan below to reactivate.
          </p>
        )}
        {status?.status === 'active' && currentPeriodEnd && (
          <p className="mt-2 text-xs text-neutral-500">
            Renews on {currentPeriodEnd.toLocaleDateString()}.
          </p>
        )}
      </div>

      {!canManageBilling && (
        <p className="text-sm text-neutral-500 max-w-2xl">
          Only an admin can change or manage the subscription plan. Contact your account admin if access is restricted.
        </p>
      )}

      {canManageBilling && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl">
        {PLANS.map((plan) => {
          const isCurrent = status?.plan === plan.key && ['active', 'trialing'].includes(status?.status);
          return (
            <div key={plan.key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-6 flex flex-col">
              <h3 className="text-sm font-semibold text-neutral-800">{plan.name}</h3>
              <p className="text-xs text-neutral-500 mt-1 mb-4">{plan.blurb}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-neutral-600">
                    <Check size={14} className="text-success-600 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                variant={isCurrent ? 'secondary' : 'primary'}
                fullWidth
                disabled={isCurrent}
                loading={busyPlan === plan.key}
                onClick={() => startCheckout(plan.key)}
              >
                {isCurrent ? 'Current plan' : 'Choose plan'}
              </Button>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default Billing;
